import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { CalendarDays, Plus, Search, MapPin, Video, Users, Camera, Clock, Filter, Loader2, ChevronLeft, ChevronRight, Edit2, Copy, Trash2 } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL || '';

const STATUS_COLORS = {
    draft: 'bg-stone-100 text-stone-600',
    scheduled: 'bg-blue-100 text-blue-700',
    in_progress: 'bg-emerald-100 text-emerald-700',
    completed: 'bg-emerald-50 text-emerald-600',
    cancelled: 'bg-red-100 text-red-700',
};

const STATUS_LABELS = {
    draft: 'Draft',
    scheduled: 'Scheduled',
    in_progress: 'In Progress',
    completed: 'Completed',
    cancelled: 'Cancelled',
};

const MODALITY_CONFIG = {
    in_person: { label: 'In Person', icon: MapPin, className: 'bg-amber-50 text-amber-700' },
    online: { label: 'Online', icon: Video, className: 'bg-violet-50 text-violet-700' },
    hybrid: { label: 'Hybrid', icon: Users, className: 'bg-sky-50 text-sky-700' },
};

const PAGE_SIZE = 25;

export default function Events() {
    const { getAuthHeader } = useAuth();
    const navigate = useNavigate();

    const [events, setEvents] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);

    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('');
    const [typeFilter, setTypeFilter] = useState('');

    const [eventTypes, setEventTypes] = useState([]);
    const [showCreate, setShowCreate] = useState(false);

    // Build a map of event type id -> display info for badges
    const typeMap = React.useMemo(() => {
        const map = {};
        eventTypes.forEach(t => {
            map[t._id || t.id || t.event_type_id] = t;
        });
        return map;
    }, [eventTypes]);

    const loadEventTypes = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/api/admin/event-types`, { headers: getAuthHeader() });
            setEventTypes(Array.isArray(res.data) ? res.data : (res.data?.data || []));
        } catch {
            // Non-critical — filter dropdown will just be empty
        }
    }, [getAuthHeader]);

    const loadEvents = useCallback(async () => {
        setLoading(true);
        try {
            const params = { page, page_size: PAGE_SIZE };
            if (statusFilter) params.status = statusFilter;
            if (typeFilter) params.event_type = typeFilter;
            if (search.trim()) params.search = search.trim();

            const res = await axios.get(`${API}/api/events`, { headers: getAuthHeader(), params });

            if (Array.isArray(res.data)) {
                setEvents(res.data);
                setTotal(res.data.length);
            } else {
                setEvents(res.data?.data || []);
                setTotal(res.data?.total || 0);
            }
        } catch {
            toast.error('Failed to load events');
        } finally {
            setLoading(false);
        }
    }, [getAuthHeader, page, statusFilter, typeFilter, search]);

    useEffect(() => { loadEventTypes(); }, [loadEventTypes]);
    useEffect(() => { loadEvents(); }, [loadEvents]);

    // Reset to page 1 when filters change
    useEffect(() => { setPage(1); }, [statusFilter, typeFilter, search]);

    const handleDuplicate = async (e, event) => {
        e.stopPropagation();
        try {
            const res = await axios.post(`${API}/api/events`, {
                name: `${event.name} (Copy)`,
                description: event.description || '',
                event_type: event.event_type || event.event_type_id || '',
                modality: event.modality || 'in_person',
                date: event.date || '',
                timezone: event.timezone || 'UTC',
                location: event.location || null,
            }, { headers: getAuthHeader() });
            toast.success('Event duplicated');
            const id = res.data?.event_id || res.data?.id;
            if (id) navigate(`/events/${id}`);
            else loadEvents();
        } catch {
            toast.error('Failed to duplicate event');
        }
    };

    const handleDelete = async (e, event) => {
        e.stopPropagation();
        if (!window.confirm(`Delete "${event.name}"? This cannot be undone.`)) return;
        try {
            await axios.delete(`${API}/api/events/${event.event_id || event._id}`, { headers: getAuthHeader() });
            toast.success('Event deleted');
            loadEvents();
        } catch {
            toast.error('Failed to delete event');
        }
    };

    const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

    const formatEventDate = (event) => {
        if (!event.start_date) return null;
        try {
            const start = new Date(event.start_date);
            const dateStr = start.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' });
            const timeStr = start.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
            const tz = event.timezone || '';
            return { dateStr, timeStr, tz };
        } catch {
            return null;
        }
    };

    const getTypeBadge = (event) => {
        const typeId = event.event_type_id || event.event_type;
        if (!typeId) return null;
        const typeInfo = typeMap[typeId];
        const label = typeInfo?.name || typeInfo?.label || typeId;
        const color = typeInfo?.color || '#78716c';
        return (
            <span
                className="text-xs px-2 py-0.5 rounded-full font-medium"
                style={{ backgroundColor: `${color}18`, color }}
            >
                {label}
            </span>
        );
    };

    const getModalityBadge = (modality) => {
        const config = MODALITY_CONFIG[modality];
        if (!config) return null;
        const Icon = config.icon;
        return (
            <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium ${config.className}`}>
                <Icon className="w-3 h-3" />
                {config.label}
            </span>
        );
    };

    return (
        <div className="min-h-screen bg-stone-50 p-6">
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h1 className="text-2xl font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Events
                    </h1>
                    <p className="text-sm text-stone-500 mt-1">Manage your marketing events, conferences, and webinars</p>
                </div>
                <Button onClick={() => setShowCreate(true)}>
                    <Plus className="w-4 h-4 mr-1.5" /> Create Event
                </Button>
            </div>

            {/* Filters */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
                <div className="relative flex-1 max-w-sm">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                    <input
                        value={search}
                        onChange={e => setSearch(e.target.value)}
                        placeholder="Search events..."
                        className="w-full pl-9 pr-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                    />
                </div>
                <div className="flex items-center gap-2">
                    <Filter className="w-4 h-4 text-stone-400" />
                    <select
                        value={statusFilter}
                        onChange={e => setStatusFilter(e.target.value)}
                        className="px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                    >
                        <option value="">All Statuses</option>
                        {Object.entries(STATUS_LABELS).map(([value, label]) => (
                            <option key={value} value={value}>{label}</option>
                        ))}
                    </select>
                    <select
                        value={typeFilter}
                        onChange={e => setTypeFilter(e.target.value)}
                        className="px-3 py-2 text-sm border border-stone-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-stone-900/20"
                    >
                        <option value="">All Types</option>
                        {eventTypes.map(t => (
                            <option key={t._id || t.id || t.event_type_id} value={t._id || t.id || t.event_type_id}>
                                {t.name || t.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            {/* Content */}
            {loading ? (
                <div className="flex items-center justify-center py-24">
                    <Loader2 className="w-8 h-8 text-stone-400 animate-spin" />
                </div>
            ) : events.length === 0 ? (
                <div className="text-center py-24">
                    <CalendarDays className="w-14 h-14 text-stone-200 mx-auto mb-4" />
                    <h3 className="text-lg font-semibold text-stone-700 mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        No events yet
                    </h3>
                    <p className="text-sm text-stone-400 mb-6">Create your first marketing event</p>
                    <Button onClick={() => setShowCreate(true)}>
                        <Plus className="w-4 h-4 mr-1.5" /> Create Event
                    </Button>
                </div>
            ) : (
                <>
                    {/* Event Cards Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {events.map(event => {
                            const dateInfo = formatEventDate(event);
                            return (
                                <div
                                    key={event.event_id || event._id}
                                    onClick={() => navigate(`/events/${event.event_id || event._id}`)}
                                    className="bg-white rounded-lg border border-stone-200 p-5 hover:shadow-md hover:border-stone-300 transition-all cursor-pointer group"
                                >
                                    {/* Top row: name + status */}
                                    <div className="flex items-start justify-between mb-2">
                                        <div className="flex items-center gap-1.5 flex-1 min-w-0 mr-2">
                                            {event.event_number && (
                                                <span className="text-[10px] font-mono font-semibold text-stone-400 shrink-0">{event.event_number}</span>
                                            )}
                                            <h3 className="text-sm font-semibold text-stone-900 group-hover:text-stone-700 truncate">
                                                {event.name || 'Untitled Event'}
                                            </h3>
                                        </div>
                                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium whitespace-nowrap ${STATUS_COLORS[event.status] || STATUS_COLORS.draft}`}>
                                            {STATUS_LABELS[event.status] || event.status || 'Draft'}
                                        </span>
                                    </div>

                                    {/* Badges row: type + modality */}
                                    <div className="flex flex-wrap items-center gap-1.5 mb-3">
                                        {getTypeBadge(event)}
                                        {getModalityBadge(event.modality)}
                                    </div>

                                    {/* Date/time */}
                                    {dateInfo && (
                                        <div className="flex items-center gap-2 text-xs text-stone-500 mb-3">
                                            <Clock className="w-3.5 h-3.5 text-stone-400 flex-shrink-0" />
                                            <span>{dateInfo.dateStr} at {dateInfo.timeStr}</span>
                                            {dateInfo.tz && (
                                                <span className="text-stone-400">({dateInfo.tz})</span>
                                            )}
                                        </div>
                                    )}

                                    {/* Stats row */}
                                    <div className="flex items-center gap-4 text-xs text-stone-500 pt-3 border-t border-stone-100">
                                        <span className="inline-flex items-center gap-1">
                                            <Users className="w-3.5 h-3.5 text-stone-400" />
                                            {event.registered_count ?? event.stats?.registered ?? 0} registered
                                        </span>
                                        <span className="inline-flex items-center gap-1">
                                            <CalendarDays className="w-3.5 h-3.5 text-stone-400" />
                                            {event.attended_count ?? event.stats?.attended ?? 0} attended
                                        </span>
                                        <span className="inline-flex items-center gap-1">
                                            <Camera className="w-3.5 h-3.5 text-stone-400" />
                                            {event.photos_count ?? event.stats?.photos ?? 0}
                                        </span>
                                    </div>

                                    {/* Action buttons */}
                                    <div className="flex items-center gap-1 pt-3 border-t border-stone-100 mt-3 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button
                                            onClick={(e) => { e.stopPropagation(); navigate(`/events/${event.event_id || event._id}`); }}
                                            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-stone-700 bg-stone-100 rounded-md hover:bg-stone-200 transition-colors"
                                        >
                                            <Edit2 className="w-3 h-3" /> Edit
                                        </button>
                                        <button
                                            onClick={(e) => handleDuplicate(e, event)}
                                            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-stone-700 bg-stone-100 rounded-md hover:bg-stone-200 transition-colors"
                                        >
                                            <Copy className="w-3 h-3" /> Duplicate
                                        </button>
                                        <button
                                            onClick={(e) => handleDelete(e, event)}
                                            className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100 transition-colors ml-auto"
                                        >
                                            <Trash2 className="w-3 h-3" /> Delete
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                        <div className="flex items-center justify-between mt-6">
                            <p className="text-xs text-stone-500">
                                Showing {((page - 1) * PAGE_SIZE) + 1}--{Math.min(page * PAGE_SIZE, total)} of {total} events
                            </p>
                            <div className="flex items-center gap-1">
                                <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={page <= 1}
                                    onClick={() => setPage(p => Math.max(1, p - 1))}
                                >
                                    <ChevronLeft className="w-4 h-4" />
                                </Button>
                                <span className="text-xs text-stone-600 px-2">
                                    Page {page} of {totalPages}
                                </span>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    disabled={page >= totalPages}
                                    onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                                >
                                    <ChevronRight className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    )}
                </>
            )}

            {/* Create Event Dialog */}
            <Dialog open={showCreate} onOpenChange={setShowCreate}>
                <DialogContent className="sm:max-w-2xl">
                    <DialogHeader>
                        <DialogTitle>Create Event</DialogTitle>
                        <DialogDescription>Set up a new marketing event</DialogDescription>
                    </DialogHeader>
                    <React.Suspense fallback={
                        <div className="flex items-center justify-center py-12">
                            <Loader2 className="w-6 h-6 text-stone-400 animate-spin" />
                        </div>
                    }>
                        <LazyEventEditor
                            onCancel={() => setShowCreate(false)}
                            onSave={(data) => {
                                setShowCreate(false);
                                const id = data?.event_id || data?.id;
                                if (id) {
                                    navigate(`/events/${id}`);
                                } else {
                                    loadEvents();
                                }
                            }}
                        />
                    </React.Suspense>
                </DialogContent>
            </Dialog>
        </div>
    );
}

const LazyEventEditor = React.lazy(() => import('./EventEditor'));
